import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { EventModel, UserInputContentList } from "@/api/model";
import {
  type UserSentEventModelOutput,
  getUserMessageMatchKey,
} from "@/utils/messages";

/**
 * "We might need it, we might need it"
 *   - @bmblum
 *
 * Update: "we needed it"
 *   - @viggyfresh
 */

export interface AuthStore {
  isAuthInitialized: boolean;
  setIsAuthInitialized: (isAuthInitialized: boolean) => void;
  anonymousUserId: string | null;
  setAnonymousUserId: (anonymousUserId: string | null) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthStore>()((set, _, store) => ({
  isAuthInitialized: false,
  setIsAuthInitialized: (isAuthInitialized) => set({ isAuthInitialized }),
  anonymousUserId: null,
  setAnonymousUserId: (anonymousUserId) => set({ anonymousUserId }),
  reset: () => {
    set(store.getInitialState());
  },
}));

export enum VideoModes {
  FULLSCREEN = "fullscreen",
  FLOATING = "floating",
  NONE = "none",
}

export interface VideoStore {
  videoMode: VideoModes;
  setVideoMode: (videoMode: VideoModes) => void;
  isVideoVisitStarted: boolean;
  setIsVideoVisitStarted: (isVideoVisitStarted: boolean) => void;
  isVideoCallFloating: boolean;
  setIsVideoCallFloating: (isVideoCallFloating: boolean) => void;
  videoCallConversationId: string | undefined;
  setVideoCallConversationId: (conversationId: string | undefined) => void;
  canJoinVideoCall: boolean;
  setCanJoinVideoCall: (canJoin: boolean) => void;
}

export const useVideoStore = create<VideoStore>()((set) => ({
  videoMode: VideoModes.NONE,
  setVideoMode: (videoMode) => set({ videoMode }),
  isVideoVisitStarted: false,
  setIsVideoVisitStarted: (isVideoVisitStarted) => set({ isVideoVisitStarted }),
  isVideoCallFloating: false,
  setIsVideoCallFloating: (isVideoCallFloating) => set({ isVideoCallFloating }),
  videoCallConversationId: undefined,
  setVideoCallConversationId: (videoCallConversationId) =>
    set({ videoCallConversationId }),
  canJoinVideoCall: false,
  setCanJoinVideoCall: (canJoinVideoCall) => set({ canJoinVideoCall }),
}));

export type NudgeType = "outreach";

export interface NudgeStore {
  conversationsWithNudgeShown: Record<string, { type: NudgeType }>;
  clearConversationNudges: () => void;
  setConversationNudge: (conversationId: string, nudgeType: NudgeType) => void;
}

export const useNudgeStore = create<NudgeStore>()((set) => ({
  conversationsWithNudgeShown: {},
  clearConversationNudges: () =>
    set((state) => {
      return {
        ...state,
        conversationsWithNudgeShown: {},
      };
    }),
  setConversationNudge: (conversationId, nudgeType) =>
    set((state) => {
      return {
        ...state,
        conversationsWithNudgeShown: {
          ...state.conversationsWithNudgeShown,
          [conversationId]: { type: nudgeType },
        },
      };
    }),
}));

export type SmsConsentNudgeFlow =
  | "video-visit"
  | "live-chat"
  | "async-rx"
  | "async-dx"
  | "rx-renewal";

export type SmsConsentNudgeState = "pending" | "shown";

export interface SmsConsentNudgeEntry {
  state: SmsConsentNudgeState;
  flow: SmsConsentNudgeFlow;
}

export interface SmsConsentNudgeStore {
  byConversation: Record<string, SmsConsentNudgeEntry>;
  triggerNudge: (conversationId: string, flow: SmsConsentNudgeFlow) => void;
  dismissNudge: (conversationId: string) => void;
}

export const useSmsConsentNudgeStore = create<SmsConsentNudgeStore>()(
  (set) => ({
    byConversation: {},
    triggerNudge: (conversationId, flow) =>
      set((state) => {
        if (state.byConversation[conversationId]) {
          return state;
        }
        return {
          byConversation: {
            ...state.byConversation,
            [conversationId]: { state: "pending", flow },
          },
        };
      }),
    dismissNudge: (conversationId) =>
      set((state) => {
        const entry = state.byConversation[conversationId];
        if (!entry) return state;
        return {
          byConversation: {
            ...state.byConversation,
            [conversationId]: { ...entry, state: "shown" },
          },
        };
      }),
  }),
);

export interface UserSentEventStore {
  conversationId?: string;
  setConversationId: (conversationId: string) => void;
  userSentEvents: UserSentEventModelOutput[];
  pushUserSentEvent: (userSentEvent: EventModel) => void;
}
export const useUserSentEventStore = create<UserSentEventStore>()(
  (set, _, store) => ({
    userSentEvents: [],
    pushUserSentEvent: (userSentEvent) =>
      set((state) => {
        if (!Array.isArray(userSentEvent.content)) {
          return state;
        }
        const newUserSentEvent: UserSentEventModelOutput = {
          ...userSentEvent,
          matchKey: getUserMessageMatchKey(
            userSentEvent.content as UserInputContentList,
          ),
        };
        return {
          userSentEvents: [...state.userSentEvents, newUserSentEvent],
        };
      }),
    conversationId: undefined,
    // If the conversation id changes, then also reset the user sent events
    setConversationId: (conversationId) =>
      set((state) => {
        return {
          conversationId,
          userSentEvents:
            state.conversationId === conversationId ? state.userSentEvents : [],
        };
      }),
  }),
);

export interface ImageGalleryStore {
  imageUris: string[];
  initialUri: string | null;
  isOpen: boolean;
  openImageGallery: (imageUris: string[], initialUri: string) => void;
  closeImageGallery: () => void;
}

export const useImageGalleryStore = create<ImageGalleryStore>()((set) => ({
  imageUris: [],
  initialUri: null,
  isOpen: false,
  openImageGallery: (imageUris, initialUri) =>
    set({ imageUris, initialUri, isOpen: true }),
  closeImageGallery: () =>
    set({ imageUris: [], initialUri: null, isOpen: false }),
}));

export interface UseCaseStore {
  conversationUseCases: Record<string, string>;
  setConversationUseCase: (conversationId: string, useCase: string) => void;
  getConversationUseCase: (
    conversationId: string | undefined,
  ) => string | undefined;
}

export const useUseCaseStore = create<UseCaseStore>()(
  persist(
    (set, get) => ({
      conversationUseCases: {},
      setConversationUseCase: (conversationId, useCase) =>
        set((state) => ({
          conversationUseCases: {
            ...state.conversationUseCases,
            [conversationId]: useCase,
          },
        })),
      getConversationUseCase: (conversationId) => {
        if (!conversationId) return undefined;
        return get().conversationUseCases[conversationId];
      },
    }),
    {
      name: "use-case-storage",
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
);

export type TalkToDoctorAiUpsellDecision =
  | { kind: "accepted"; messageId: string }
  | { kind: "declined" };

export interface TalkToDoctorAiUpsellStore {
  decisionByConversationId: Record<string, TalkToDoctorAiUpsellDecision>;
  setUpsellDecision: (
    conversationId: string,
    decision: TalkToDoctorAiUpsellDecision,
  ) => void;
  getUpsellDecision: (
    conversationId: string | undefined,
  ) => TalkToDoctorAiUpsellDecision | undefined;
}

export const useTalkToDoctorAiUpsellStore = create<TalkToDoctorAiUpsellStore>()(
  persist(
    (set, get) => ({
      decisionByConversationId: {},
      setUpsellDecision: (conversationId, decision) =>
        set((state) => ({
          decisionByConversationId: {
            ...state.decisionByConversationId,
            [conversationId]: decision,
          },
        })),
      getUpsellDecision: (conversationId) => {
        if (!conversationId) return undefined;
        return get().decisionByConversationId[conversationId];
      },
    }),
    {
      name: "talk-to-doctor-ai-upsell-storage",
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
);

export const useHasTalkToDoctorAiUpsellStoreHydrated = (): boolean => {
  const [hasHydrated, setHasHydrated] = useState(() =>
    useTalkToDoctorAiUpsellStore.persist.hasHydrated(),
  );
  useEffect(() => {
    const unsub = useTalkToDoctorAiUpsellStore.persist.onFinishHydration(() =>
      setHasHydrated(true),
    );
    if (useTalkToDoctorAiUpsellStore.persist.hasHydrated()) {
      setHasHydrated(true);
    }
    return unsub;
  }, []);
  return hasHydrated;
};

// Re-export ask user questions store
export * from "./askUserQuestions";
// Re-export pre-clinic steps store
export * from "./preClinicSteps";
// Re-export rx questionnaire store
export * from "./rxQuestionnaire";
export * from "./theme";
